import ViewViewModel from '@/ViewModels/ViewViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import Loader from '@/Utility/Loader';
import RADApplication from '@/Management/RADApplication';
import LinkViewModelT from '@/ViewModels/Controls/LinkViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import WindowViewModelT from '@/ViewModels/Controls/WindowViewModel';
import GridViewModelT from '@/ViewModels/Controls/GridViewModel';
import ButtonViewModelT from '@/ViewModels/Controls/ButtonViewModel';
import Config from '@/Management/GlobalConfiguration';
import ListViewHelper from '@/Utility/ListViewHelper';

export default class TableConfigViewModel extends ViewViewModel {

    private WindowViewModel = ComponentManager.getViewModel<typeof WindowViewModelT>('WindowViewModel');
    private GridViewModel = ComponentManager.getViewModel<typeof GridViewModelT>('GridViewModel');
    private ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModelT>('ButtonViewModel');
    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');

    /**
     * Die bisher gespeicherte Konfiguration
     */
    public columnDefinition: KnockoutObservableArray<any>;

    /**
     * Die Default-Konfiguration
     */
    public defaultDefinition: KnockoutObservableArray<any>;

    /**
     * Der Key unter dem die Config in UserSpecificData verwaltet wird
     */
    public userSpecificDataKey;

    /**
     * Handler für das Schließen des Fensters
     */
    public onClose: Function;

    /**
     * Das Fenster
     */
    public window: WindowViewModelT;

    /**
     * Das Label für die verfügbaren Spalten
     */
    public availableLabel: LabelViewModelT;

    /**
     * Das Grid mit den verfügbaren Spalten
     */
    public available: GridViewModelT;

    /**
     * Das Label für die sichtbaren Spalten
     */
    public visibleLabel: LabelViewModelT;

    /**
     * Das Grid mit den sichtbaren Spalten
     */
    public visible: GridViewModelT;

    /**
     * Der Link zum Verschieben einer Spalte in die Tabelle mit sichtbaren Elementen
     */
    public moveRight: LinkViewModelT;

    /**
     * Der Link zum Verschieben einer Spalte in die Tabelle mit verfügbaren Elementen
     */
    public moveLeft: LinkViewModelT;

    /**
     * Der Link zum Verschieben aller Spalten in die Tabelle mit sichtbaren Elementen
     */
    public allMoveRight: LinkViewModelT;

    /**
     * Der Link zum Verschieben aller Spalten in die Tabelle mit verfügbaren Elementen
     */
    public allMoveLeft: LinkViewModelT;

    /**
     * Der Link zum Umsortieren einer Spalte (nach unten)
     */
    public moveDown: LinkViewModelT;

    /**
     * Der Link zum Umsortieren einer Spalte (nach oben)
     */
    public moveUp: LinkViewModelT;

    /**
     * Der Button zum Bestätigen der Änderungen
     */
    public okay: ButtonViewModelT;

    /**
     * Der Button zum Abbrechen
     */
    public cancel: ButtonViewModelT;

    /**
     * Der Button zum Wiederherstellen der zuvor gespeicherten Konfiguration
     */
    public reset: ButtonViewModelT;

    public scrollObservable: KnockoutObservable<number>;

    constructor(params) {
        super(params);

        this.undisposeProperties.push('columnDefinition');
        this.columnDefinition = this.observableOrDefault(params.columnDefinition, []) as KnockoutObservableArray<any>;
        this.defaultDefinition = this.paramOrDefault(params.defaultDefinition, []) as KnockoutObservableArray<any>;
        this.userSpecificDataKey = this.paramOrDefault(params.userSpecificDataKey, null);
        this.onClose = this.handlerOrDefault(params.onClose);

        this.window = new this.WindowViewModel({
            isOpen: true,
            allowClose: true,
            parentLocator: this.locator,
            locator: 'window',
            headline: new this.LabelViewModel({
                ergName: 'CUSTOM_COLUMN_WINDOW_TITLE'
            }),
            isModal: true,
            width: 658,
            onClose: this.onClose
        });

        this.availableLabel = new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'available.label',
            ergName: 'CUSTOM_COLUMN_AVAILABLE_COLUMNS'
        });

        this.available = new this.GridViewModel({
            parentLocator: this.locator,
            locator: 'available.grid',
            columns: [{
                name: 'title',
                field: undefined,
                title: undefined,
                isSortable: false,
                filterable: false,
                hidden: false,
                width: '100%',
                value: (row) => {
                    return row.mlsErgName[ResourceManager.getInstance().language.value()];
                }
            }],
            identity: 'id',
            pageSizes: [100],
            pageSize: 100,
            page: 1,
            onDoubleClickHandler: (pdoId) => {
                this.moveItemsRight([pdoId]);
            },
            showHeader: false,
            renderGridAsync: false
        });

        this.visibleLabel = new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'visible.label',
            ergName: 'CUSTOM_COLUMN_VISIBLE_COLUMNS'
        });

        this.visible = new this.GridViewModel({
            parentLocator: this.locator,
            locator: 'visible.grid',
            columns: [{
                name: 'title',
                field: undefined,
                title: undefined,
                isSortable: false,
                filterable: false,
                hidden: false,
                width: '100%',
                value: (row) => {
                    return row.mlsErgName[ResourceManager.getInstance().language.value()];
                }
            }],
            identity: 'id',
            pageSizes: [100],
            pageSize: 100,
            page: 1,
            onDoubleClickHandler: (pdoId) => {
                this.moveItemsLeft([pdoId]);
            },
            showHeader: false,
            renderGridAsync: false
        });

        this.moveRight = new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'move-right',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'move-right.image',
                source: Config.inlineImages.NAVIGATE_RIGHT,
                alternativeText: new this.LabelViewModel({
                    ergName: 'MOVE_RIGHT'
                })
            }),
            onClick: () => {
                this.moveItemsRight(this.available.selected());
            },
            isEnabled: this.defineComputed(() => {
                return this.available.selected().length > 0;
            }),
            isToolBarLink: true
        });

        this.moveLeft = new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'move-left',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'move-left.image',
                source: Config.inlineImages.NAVIGATE_LEFT,
                alternativeText: new this.LabelViewModel({
                    ergName: 'MOVE_LEFT'
                })
            }),
            onClick: () => {
                this.moveItemsLeft(this.visible.selected());
            },
            isEnabled: this.defineComputed(() => {
                return this.visible.selected().length > 0;
            }),
            isToolBarLink: true
        });

        this.allMoveRight = new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'move-all-right',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'move-all-right.image',
                source: Config.inlineImages.NAVIGATE_RIGHT_ALL,
                alternativeText: new this.LabelViewModel({
                    ergName: 'MOVE_ALL_RIGHT'
                })
            }),
            onClick: () => {
                let columns = [];
                let visibleData = this.visible.data();
                let availableData = this.available.data();

                _.each(visibleData, (visible) => {
                    columns.push(visible);
                });

                _.each(availableData, (available) => {
                    columns.push(available);
                });

                this.visible.data(columns);
                this.available.data([]);
                this.visible.selected([]);
                this.available.selected([]);
            },
            isEnabled: this.defineComputed(() => {
                return this.available.data().length > 0;
            }),
            isToolBarLink: true
        });

        this.allMoveLeft = new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'move-all-left',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'move-all-left.image',
                source: Config.inlineImages.NAVIGATE_LEFT_ALL,
                alternativeText: new this.LabelViewModel({
                    ergName: 'MOVE_ALL_LEFT'
                })
            }),
            onClick: () => {
                let columns = [];
                let visibleData = this.visible.data();
                let availableData = this.available.data();

                _.each(visibleData, (visible) => {
                    columns.push(visible);
                });

                _.each(availableData, (available) => {
                    columns.push(available);
                });

                this.available.data(columns);
                this.visible.data([]);
                this.visible.selected([]);
                this.available.selected([]);
            },
            isEnabled: this.defineComputed(() => {
                return this.visible.data().length > 0;
            }),
            isToolBarLink: true
        });

        this.moveUp = new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'move-up',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'move-up.image',
                source: 'arrow_up.svg',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MOVE_UP'
                })
            }),
            onClick: () => {
                let selected = this.visible.selected();

                for (let i = 0; i < selected.length; i++) {
                    let data = this.visible.data();
                    let index = -1;

                    for (let j = 0; j < data.length; j++) {
                        if (data[j].id === selected[i]) {
                            index = j;
                            break;
                        }
                    }

                    if (index > 0) {
                        this.visible.data.swap(index, index - 1);
                    }
                }
            },
            isEnabled: this.defineComputed(() => {
                let items = this.visible.data();
                let firstItem = items[0];
                if (!firstItem) {
                    return false;
                }
                let selection = this.visible.selected();
                //nur enablen wenn nicht mehr als 1 Element markiert wurde und dieses nicht schon an erster Stelle steht
                if (selection.length === 1 && firstItem.id != selection[0]) {
                    return true;
                }

                return false;
            }),
            isToolBarLink: true
        });

        this.moveDown = new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'move-down',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'move-down.image',
                source: 'arrow_down.svg',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MOVE_DOWN'
                })
            }),
            onClick: () => {
                let selected = this.visible.selected();

                for (let i = selected.length - 1; i >= 0; i--) {
                    let data = this.visible.data();
                    let index = -1;

                    for (let j = 0; j < data.length; j++) {
                        if (data[j].id === selected[i]) {
                            index = j;
                            break;
                        }
                    }

                    if (index >= 0 && index < data.length - 1) {
                        this.visible.data.swap(index, index + 1);
                    }
                }
            },
            isEnabled: this.defineComputed(() => {
                let items = this.visible.data();
                let lastItem = items[items.length - 1];
                if (!lastItem) {
                    return false;
                }
                let selection = this.visible.selected();
                //nur enablen wenn nicht mehr als 1 Element markiert wurde und dieses nicht schon an letzter Stelle steht
                if (selection.length === 1 && lastItem.id != selection[0]) {
                    return true;
                }

                return false;
            }),
            isToolBarLink: true
        });

        this.okay = new this.ButtonViewModel({
            parentLocator: this.locator,
            locator: 'ok',
            isPrimaryButton: true,
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'ok.label',
                ergName: 'OKAY'
            }),
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'ok.image',
                source: Config.inlineImages.WINDOW_OK,
                alternativeText: new this.LabelViewModel({
                    ergName: 'OKAY'
                })
            }),
            isEnabled: this.defineComputed(() => {
                return this.visible.data().length > 0;
            }),
            onClick: () => {
                Loader.getInstance().show();
                let columns = [];
                let visibleData = this.visible.data();
                let availableData = this.available.data();
                let columnNames = [];
                let availableColumnNames = [];

                _.each(visibleData, (visible: any) => {
                    visible.isVisible = true;
                    columns.push(visible);
                    columnNames.push(visible.name);
                });

                _.each(availableData, (available: any) => {
                    available.isVisible = false;
                    columns.push(available);
                    availableColumnNames.push(available.name);
                });

                columns.forEach(x => {
                    delete x['id'];
                    delete x['title'];
                });
                let colDefs = this.columnDefinition;
                window.setTimeout(() => {
                    Loader.getInstance().hide();
                    colDefs(columns);
                })

                if (this.reset.isEnabled()) {
                    this.setUserSpecificData(ListViewHelper.createColumnDefinitionString(columnNames, availableColumnNames));
                }
                else {
                    this.setUserSpecificData(null);
                }

                this.window.isOpen(false);
            }
        });


        this.cancel = new this.ButtonViewModel({
            parentLocator: this.locator,
            locator: 'cancel',
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'cancel.label',
                ergName: 'CANCEL'
            }),
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'cancel.image',
                source: Config.inlineImages.WINDOW_CANCEL,
                alternativeText: new this.LabelViewModel({
                    ergName: 'CANCEL'
                })
            }),
            onClick: (callback) => {
                this.window.isOpen(false);
            }
        });

        this.reset = new this.ButtonViewModel({
            parentLocator: this.locator,
            locator: 'reset',
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'reset.label',
                ergName: 'CUSTOM_COLUMN_DEFAULT_VALUES'
            }),
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'reset.image',
                source: Config.inlineImages.RESET,
                alternativeText: new this.LabelViewModel({
                    ergName: 'REVERT_BUTTON_TOOLTIP'
                })
            }),
            onClick: () => {
                let columns = this.defaultDefinition;

                let visible = [];
                let available = [];

                for (let i in columns) {
                    let column = $.extend(true, {}, columns[i]);

                    column.id = i;
                    column.title = column.mlsErgName[ResourceManager.getInstance().language.value()];

                    if (column.isVisible) {
                        visible.push(column);
                    } else {
                        available.push(column);
                    }
                }

                this.visible.data(visible);
                this.available.data(available);

                this.setUserSpecificData(null);
            },
            isEnabled: this.defineComputed(() => {
                let items = this.visible.data();
                let defaultItems = this.getVisibleDefaultColumns(this.defaultDefinition);

                if (this.itemsAreEqualToDefault(items, defaultItems)) {
                    return false;
                }
                return true;
            })
        });

        this.scrollObservable = this.observableOrDefault(0);

        this.defineComputed(() => {
            const data = this.available.data();

            data.sort((a, b) => {
                return a.mlsErgName[ResourceManager.getInstance().language.value()].localeCompare(b.mlsErgName[ResourceManager.getInstance().language.value()]);
            });

            this.available.data.valueHasMutated();
        });


        (() => {
            let columns = this.columnDefinition();

            let visible = [];
            let available = [];

            for (let i in columns) {
                let column = jQuery.extend(true, {}, columns[i]);

                column.id = i;
                column.title = column.mlsErgName[ResourceManager.getInstance().language.value()];

                if (column.isVisible) {
                    visible.push(column);
                }
                else {
                    available.push(column);
                }
            }

            this.visible.data(visible);
            this.available.data(available);

        })();
    }

    private moveItemsRight(pdoIds: Array<string>) {
        let availableSelected: Array<string>;
        if (!pdoIds) {
            availableSelected = this.available.selected();
        } else
            availableSelected = pdoIds;

        let availableData = this.available.data();

        // two things are happening here (which is bad)
        // 1 in available data all rows are stored that were not selected
        // 2 all other rows are pushed to self.visible.data
        availableData = _.filter(availableData, (data: any) => {
            if (availableSelected.indexOf(data.id) === -1) {
                return true;
            } else {
                this.visible.data.push(data);
                return false;
            }
        });

        // determine index 
        let firstSelectedElement = availableSelected[0];
        let indexOfMovedRow = _.findIndex(this.available.data(), (e: any) => {
            return e.id === firstSelectedElement;
        });

        // update data
        this.available.data(availableData);

        // update scroll position
        if (this.scrollObservable() === indexOfMovedRow) {
            this.scrollObservable.valueHasMutated();
        } else {
            this.scrollObservable(indexOfMovedRow);
        }

        // select element at the position of the moved-row
        if (!_.isEmpty(availableData)) {
            let newSelectedElement = availableData[indexOfMovedRow];
            if (!newSelectedElement) {
                let lastPosition = availableData.length - 1;
                let firstPosition = 0;
                let fallbackIndex = indexOfMovedRow > 0 ? lastPosition : firstPosition;
                newSelectedElement = availableData[fallbackIndex];
            }

            this.available.selected([newSelectedElement.id]);
        }
        else {
            this.available.selected([]);
        }

        this.visible.selected([]);
    }

    private moveItemsLeft(pdoIds: Array<string>) {
        let visibleSelected: Array<string>;
        if (!pdoIds) {
            visibleSelected = this.visible.selected();
        } else
            visibleSelected = pdoIds;

        let visibleData = this.visible.data();
        const availableData = this.available.data();

        visibleData = _.filter(visibleData, (data: any) => {
            if (visibleSelected.indexOf(data.id) === -1) {
                return true;
            } else {
                availableData.push(data);
                return false;
            }
        });

        // Da das Array schon im Hintergrund bearbeitet wird und nicht die .push-Methode des Observables genutzt wird,
        // müssen wir manuell feuern, dass es sich geändert hat.
        this.available.data.valueHasMutated();

        this.visible.data(visibleData);
        this.visible.selected([]);
        this.available.selected([]);
    }

    private setUserSpecificData(value): void {
        if (!String.isNullOrEmpty(this.userSpecificDataKey)) {
            RADApplication.getInstance().user.setUserSpecificData(this.userSpecificDataKey, value);
        }
    }

    private itemsAreEqualToDefault(items: any[], defaultItems: any[]): boolean {
        if (!items || !defaultItems) {
            return false;
        }
        if (items.length != defaultItems.length) {
            return false;
        }
        for (let i = 0; i < items.length; i++) {
            let item = items[i];
            let defaultItem = defaultItems[i];

            if (item.name != defaultItem.name) {
                return false;
            }

        }
        return true;
    }

    private getVisibleDefaultColumns(columns): Array<any> {
        let ret = [];
        for (let i in columns) {
            let column = $.extend(true, {}, columns[i]);

            column.id = i;
            column.title = column.mlsErgName[ResourceManager.getInstance().language.value()];

            if (column.isVisible) {
                ret.push(column);
            }
        }
        return ret;
    }

}

ComponentManager.setViewModel('TableConfigViewModel', TableConfigViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');